import { Fragment, useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import ReactECharts from "echarts-for-react";
import { useDispatch, useSelector } from "react-redux";
import createBatteryOption from "./graphs-config/battery";
import humidityOptionsCreator from "./graphs-config/humidity";
import createPressureOption from "./graphs-config/pressure";
import createTemperatureOption from "./graphs-config/temperature";
import createrssiHistoryOptions from "./graphs-config/rssi_history";
import createsignalHistoryOptions from "./graphs-config/signal_history";
import createlightOption from "./graphs-config/light";
import createtiltOption from "./graphs-config/tilt";
import {
  fetchBatteryHistoryRequest,
  fetchHumidityRequest,
  fetchLightRequest,
  fetchPressureRequest,
  fetchRSSIHistoryRequest,
  fetchSignalHistoryRequest,
  fetchTemperatureRequest,
  fetchTiltRequest,
} from "../../../store/actions/deviceShipmentsDashboard";
import { isMoreThan7Days } from "../../../global/utility/GmtToLocal";
import dayjs from "dayjs";

function ViewDeviceShipmentDashboard(props) {
  const dispatch = useDispatch();
  const {
    id,
    startDateTime,
    endDateTime,
    gateway,
    shipBleDeviceId,
    shipmentData,
  } = props;

  const deviceName = useSelector(
    (state) => state?.shipment?.data?.deviceName?.devices
  );
  const deviceType = useSelector(
    (state) => state?.shipment?.data?.deviceType?.devicetypes
  );
  const shipmetProfile = useSelector(
    (state) => state?.shipment?.data?.shipProfile
  );

  const matchedDevice = deviceName?.find((device) => device?.uid === id);
 //const deviceTypeVal = matchedDevice?.devicetype || null;
  const deviceTypeVal = matchedDevice?.devicetype ?? shipmentData?.devicetype;
  const matchedDevice1 = deviceType?.find(
    (device) => device?.devicetype === deviceTypeVal
  );

  const hasFetched = useRef(false);
  const [hasLatestrecord, sethasLatestrecord] = useState([]);

  const chartRefs = {
    battery: useRef(null),
    temperature: useRef(null),
    pressure: useRef(null),
    humidity: useRef(null),
    light: useRef(null),
    tilt: useRef(null),
    signal: useRef(null),
    rssi: useRef(null),
  };

  const yAxisLabelsRefs = {
    battery: useRef([]),
    temperature: useRef([]),
    pressure: useRef([]),
    humidity: useRef([]),
    light: useRef([]),
    tilt: useRef([]),
    signal: useRef([]),
    rssi: useRef([]),
  };

  const extractYAxisLabels = (type, chartRef, data) => {
    if (!Array.isArray(data) || data.length === 0 || !chartRef.current) return;
    try {
      const chart = chartRef.current;
      const option = chart.getOption();
      const yAxis = chart.getModel().getComponent("yAxis").axis;
      const ticks = yAxis?.scale?.getTicks?.();
      const labels = ticks?.map((tick) => yAxis.scale.getLabel(tick)) || [];
      if (type === "temperature") {
        sethasLatestrecord(labels);
      }
      if (
        labels.length > 1 &&
        labels.every((label) => !isNaN(Number(label.replace(/,/g, ""))))
      ) {
        yAxisLabelsRefs[type].current = labels;
      }
    } catch (err) {}
  };
  const result = {
    devicemodel: matchedDevice1?.devicemodel,
    attributes: matchedDevice1?.attributes?.map((attr) => attr.name),
  };

  let bleProfileId = null;
  const bleDevices = Array.isArray(shipmentData?.bledevice)
    ? shipmentData.bledevice
    : [];
  if (result?.devicemodel === "BLE") {
    const matchedBleDevice = bleDevices?.find((device) => device.id === id);
    if (matchedBleDevice) bleProfileId = matchedBleDevice?.profile;
  }

  const matchingProfile = shipmetProfile?.find(
    (profile) =>
      profile?.profileid ===
      (result?.devicemodel === "BLE"
        ? bleProfileId
        : shipmentData?.deviceprofile?.id)
  );
  const attributes = matchingProfile?.attributes || [];
  const getAttributeByName = (attributes, name) => {
    if (!attributes || !Array.isArray(attributes)) return undefined;
    if (Array.isArray(name)) {
      for (const n of name) {
        const foundAttr = attributes.find((attr) => attr?.name === n);
        if (foundAttr) return foundAttr;
      }
      return undefined;
    }
    return attributes.find((attr) => attr.name === name);
  };

  const selectors = useSelector((state) => state.shipmentDashboard);
  const { isLoading, data } = selectors;

  const dataMap = {
    battery: data.batteryHistory?.battery,
    temperature: data.Temperature?.temperature,
    pressure: data.Pressure?.pressure,
    humidity: data.Humidity?.humidity,
    light: data.Light?.light,
    tilt: data.Tilt?.tilt,
    signal: data.signalHistory,
    rssi: data.rssiHistory?.rssi,
  };

  const attributeMap = {
    battery: getAttributeByName(attributes, "Battery"),
    temperature: getAttributeByName(attributes, "Temperature"),
    pressure: getAttributeByName(attributes, "Pressure"),
    humidity: getAttributeByName(attributes, "Humidity"),
    light: getAttributeByName(attributes, "Light"),
    tilt: getAttributeByName(attributes, "Tilt"),
    signal: getAttributeByName(attributes, [
      "Cellular Signal Strength",
      "rfSignal",
    ]),
    rssi: getAttributeByName(attributes, "Cellular Signal Strength"),
  };

  const chartCreators = {
    battery: createBatteryOption,
    temperature: createTemperatureOption,
    pressure: createPressureOption,
    humidity: humidityOptionsCreator,
    light: createlightOption,
    tilt: createtiltOption,
    signal: createsignalHistoryOptions,
    rssi: createrssiHistoryOptions,
  };

  const fetchActions = [
    fetchBatteryHistoryRequest,
    fetchHumidityRequest,
    fetchLightRequest,
    fetchPressureRequest,
    fetchRSSIHistoryRequest,
    fetchSignalHistoryRequest,
    fetchTemperatureRequest,
    fetchTiltRequest,
  ];

  useEffect(() => {
if (id && !hasFetched.current && shipBleDeviceId === id) {
    hasFetched.current = true;
    let sDateTime = isMoreThan7Days(
      startDateTime || Date.now(),
      endDateTime || Date.now()
    )
      ? dayjs(endDateTime || Date.now())
          .subtract(7, "day")
          .toISOString()
      : dayjs(startDateTime).toISOString();

    fetchActions.forEach((action) =>
      dispatch(action({ id, startDateTime: sDateTime, endDateTime }))
    );
  }
  }, [dispatch, id]);

  useEffect(() => {
    Object.entries(chartRefs).forEach(([key, ref]) => {
      extractYAxisLabels(key, ref, dataMap[key]);
    });
  }, [data]);

  const getChart = (key, label, height = "350px") => {
    if (!(result?.devicemodel === "GAT" || result?.attributes?.includes(label) || result?.devicemodel === "BLE"))
      return null;
    const option = chartCreators[key](
      attributeMap[key],
      Array.isArray(dataMap[key]) ? [...dataMap[key]] : [],
      yAxisLabelsRefs[key].current
    );

    return (
      <div className="row pt-2" key={key}>
        <div className="view-device-dashboard-full signalHistoryBLE">
          <div className="d-flex flex-column">
            <div className="d-flex flex-row justify-content-between align-items-center">
              <h3 className="single-device-card-title">{label}</h3>
            </div>
            <div>
              <ReactECharts
                showLoading={isLoading[key]}
                style={{ height }}
                option={{
                  ...option,
                  series: [
                    {
                      ...option?.series?.[0],
                      data: Array.isArray(dataMap[key])
                        ? [...dataMap[key]]
                        : [],
                    },
                  ],
                }}
                notMerge={true}
                opts={{ renderer: "svg" }}
                onChartReady={(chart) => {
                  chartRefs[key].current = chart;
                }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Fragment>
      {getChart("battery", "Battery")}
      {getChart("temperature", "Temperature")}
      {getChart("pressure", "Pressure")}
      {getChart("humidity", "Humidity")}
      {result?.devicemodel === "GAT" && getChart("light", "Light")}
      {result?.devicemodel === "GAT" && getChart("tilt", "Tilt")} 
      {result?.devicemodel === "GAT" && getChart("signal", "Signal Strength")}
      {result?.devicemodel === "BLE" && getChart("rssi", "RSSI", "399px")}
    </Fragment>
  );
}
ViewDeviceShipmentDashboard.propTypes = {
  isFullScreenMode: PropTypes.bool.isRequired,
  width: PropTypes.number.isRequired,
};
export default ViewDeviceShipmentDashboard;
